import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDialog } from '@shared/hooks/useDialog';
import { planApi } from '@features/billing/api/planApi';
import type { Plan, CreatePlanRequest } from '@features/billing/types/plan';
import { formatPrice } from '@features/billing/types/plan';
import { Button, Card, Badge, Input } from '@shared/components/ui';
import {
  Plus,
  Edit2,
  Trash2,
  Users,
  HardDrive,
  Package,
  Check,
  X,
  Loader2,
  CreditCard,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react';

const SUPPORT_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'priority', label: 'Priority' },
  { value: 'phone', label: 'Phone' },
];

export default function PlanManagement() {
  const { t, i18n } = useTranslation('admin');
  const formatPlanPrice = (cents: number, currency?: string) =>
    formatPrice(cents, currency || 'BRL', i18n.resolvedLanguage || i18n.language);
  const { showConfirm } = useDialog();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [syncingPlanId, setSyncingPlanId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreatePlanRequest & { features: any }>({
    name: '',
    displayName: '',
    description: '',
    monthlyPrice: 0,
    currency: 'BRL',
    agentsIncluded: 1,
    storageIncludedGB: 10,
    maxAgents: undefined,
    extraAgentPrice: undefined,
    extraAgentStorageGB: undefined,
    extraStoragePrice: 0,
    extraStorageAmountGB: 10,
    features: {
      retentionDays: 7,
      support: 'email',
      apiAccess: false,
    },
    isActive: true,
    isPublic: true,
    sortOrder: 0,
    syncWithStripe: false,
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadPlans();
    checkStripeStatus();
  }, []);

  const checkStripeStatus = async () => {
    try {
      const status = await planApi.getStripeStatus();
      setStripeEnabled(status.enabled);
    } catch {
      setStripeEnabled(false);
    }
  };

  const loadPlans = async () => {
    try {
      const data = await planApi.getPlans();
      setPlans(data);
      setLoadError(false);
      setMessage((current) => (current?.type === 'error' ? null : current));
    } catch {
      setLoadError(true);
      setMessage({ type: 'error', text: t('planManagement.errors.load') });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      await planApi.createPlan({
        ...formData,
        features: formData.features,
      });
      setShowForm(false);
      resetForm();
      loadPlans();
      setMessage({ type: 'success', text: t('planManagement.messages.created') });
    } catch {
      setMessage({ type: 'error', text: t('planManagement.errors.create') });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingPlan) return;
    setSaving(true);
    try {
      await planApi.updatePlan(editingPlan.id, {
        ...formData,
        features: formData.features,
      });
      setEditingPlan(null);
      setShowForm(false);
      resetForm();
      loadPlans();
      setMessage({ type: 'success', text: t('planManagement.messages.updated') });
    } catch {
      setMessage({ type: 'error', text: t('planManagement.errors.update') });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan: Plan) => {
    const confirmed = await showConfirm(
      t('planManagement.confirmDeleteTitle'),
      t('planManagement.confirmDeleteMessage', { name: plan.displayName }),
      'warning'
    );
    if (!confirmed) return;

    try {
      await planApi.deletePlan(plan.id);
      loadPlans();
      setMessage({ type: 'success', text: t('planManagement.messages.deleted') });
    } catch {
      setMessage({ type: 'error', text: t('planManagement.errors.delete') });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      displayName: '',
      description: '',
      monthlyPrice: 0,
      currency: 'BRL',
      agentsIncluded: 1,
      storageIncludedGB: 10,
      maxAgents: undefined,
      extraAgentPrice: undefined,
      extraAgentStorageGB: undefined,
      extraStoragePrice: 0,
      extraStorageAmountGB: 10,
      features: {
        retentionDays: 7,
        support: 'email',
        apiAccess: false,
      },
      isActive: true,
      isPublic: true,
      sortOrder: 0,
      syncWithStripe: false,
    });
  };

  const startEdit = (plan: Plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      displayName: plan.displayName,
      description: plan.description,
      monthlyPrice: plan.monthlyPrice,
      currency: plan.currency,
      agentsIncluded: plan.agentsIncluded,
      storageIncludedGB: plan.storageIncludedGB,
      maxAgents: plan.maxAgents,
      extraAgentPrice: plan.extraAgentPrice,
      extraAgentStorageGB: plan.extraAgentStorageGB,
      extraStoragePrice: plan.extraStoragePrice,
      extraStorageAmountGB: plan.extraStorageAmountGB,
      features: plan.features || { retentionDays: 7, support: 'email', apiAccess: false },
      stripePriceId: plan.stripePriceId,
      stripeProductId: plan.stripeProductId,
      isActive: plan.isActive,
      isPublic: plan.isPublic,
      sortOrder: plan.sortOrder,
      baseMargin: plan.baseMargin,
      syncWithStripe: false,
    });
    setShowForm(true);
  };

  const handleSyncWithStripe = async (plan: Plan) => {
    setSyncingPlanId(plan.id);
    try {
      const result = await planApi.syncPlanWithStripe(plan.id);
      if (result.success) {
        setMessage({
          type: 'success',
          text: t('planManagement.messages.synced', { name: plan.displayName }),
        });
        loadPlans();
      } else {
        setMessage({ type: 'error', text: t('planManagement.errors.partialSync') });
      }
    } catch {
      setMessage({ type: 'error', text: t('planManagement.errors.sync') });
    } finally {
      setSyncingPlanId(null);
    }
  };

  const updateFeature = (key: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      features: { ...prev.features, [key]: value },
    }));
  };

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-brand-600 dark:text-brand-400" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="app-page-title">{t('planManagement.title')}</h1>
            <p className="text-text-muted-light dark:text-text-muted-dark">
              {t('planManagement.description')}
            </p>
          </div>
          <Button
            className="whitespace-nowrap"
            onClick={() => {
              setShowForm(true);
              setEditingPlan(null);
              resetForm();
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('planManagement.newPlan')}
          </Button>
        </div>

        {/* Message */}
        {message && (
          <div
            role={message.type === 'error' ? 'alert' : 'status'}
            className={`p-4 rounded-xl border ${
              message.type === 'success'
                ? 'bg-state-ok-bg-light dark:bg-state-ok-bg-dark border-state-ok-light/20 dark:border-state-ok-dark/20 text-state-ok-light dark:text-state-ok-dark'
                : 'bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-state-danger-light/20 dark:border-state-danger-dark/20 text-state-danger-light dark:text-state-danger-dark'
            }`}
          >
            {message.text}
            {loadError && (
              <Button type="button" className="mt-3 min-h-11" variant="outline" onClick={loadPlans}>
                {t('planManagement.errors.retry')}
              </Button>
            )}
          </div>
        )}

        {!loading && plans.length === 0 && !loadError && (
          <Card className="p-8 text-center">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">
              {t('planManagement.emptyTitle')}
            </h2>
            <p className="mt-1 text-text-muted-light dark:text-text-muted-dark">
              {t('planManagement.emptyDescription')}
            </p>
          </Card>
        )}

        {/* Plan Form Modal */}
        {showForm && (
          <Card className="p-6">
            <form
              aria-labelledby="plan-form-title"
              onSubmit={(event) => {
                event.preventDefault();
                if (editingPlan) {
                  void handleUpdate();
                } else {
                  void handleCreate();
                }
              }}
            >
              <h2 id="plan-form-title" className="text-lg font-bold mb-4">
                {editingPlan ? t('planManagement.editPlan') : t('planManagement.createPlan')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="plan-internal-name" className="block text-sm font-medium mb-1">
                    {t('planManagement.fields.internalName')}
                  </label>
                  <Input
                    id="plan-internal-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t('planManagement.placeholders.internalName')}
                    disabled={!!editingPlan}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="plan-sort-order" className="block text-sm font-medium mb-1">
                    {t('planManagement.fields.sortOrder')}
                  </label>
                  <Input
                    id="plan-sort-order"
                    type="number"
                    value={formData.sortOrder}
                    onChange={(e) =>
                      setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })
                    }
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="plan-display-name" className="block text-sm font-medium mb-1">
                    {t('planManagement.fields.displayName')}
                  </label>
                  <Input
                    id="plan-display-name"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    placeholder={t('planManagement.placeholders.displayName')}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="plan-currency" className="block text-sm font-medium mb-1">
                    {t('planManagement.fields.currency')}
                  </label>
                  <select
                    id="plan-currency"
                    className="w-full min-h-11 px-3 py-2 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-lg"
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  >
                    <option value="BRL">BRL (R$)</option>
                    <option value="USD">USD ($)</option>
                  </select>
                </div>
                <div className="md:col-span-3">
                  <label htmlFor="plan-description" className="block text-sm font-medium mb-1">
                    {t('planManagement.fields.description')}
                  </label>
                  <Input
                    id="plan-description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={t('planManagement.placeholders.description')}
                  />
                </div>
                <div>
                  <label htmlFor="plan-monthly-price" className="block text-sm font-medium mb-1">
                    {t('planManagement.fields.monthlyPrice')}
                  </label>
                  <Input
                    id="plan-monthly-price"
                    type="number"
                    value={formData.monthlyPrice}
                    onChange={(e) =>
                      setFormData({ ...formData, monthlyPrice: parseInt(e.target.value) || 0 })
                    }
                    required
                  />
                  <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-1">
                    {formatPlanPrice(formData.monthlyPrice, formData.currency)}
                  </p>
                </div>
                <div>
                  <label htmlFor="plan-agents-included" className="block text-sm font-medium mb-1">
                    {t('planManagement.fields.agentsIncluded')}
                  </label>
                  <Input
                    id="plan-agents-included"
                    type="number"
                    value={formData.agentsIncluded}
                    onChange={(e) =>
                      setFormData({ ...formData, agentsIncluded: parseInt(e.target.value) || 0 })
                    }
                    required
                  />
                </div>
                <div>
                  <label htmlFor="plan-storage-included" className="block text-sm font-medium mb-1">
                    {t('planManagement.fields.storageIncluded')}
                  </label>
                  <Input
                    id="plan-storage-included"
                    type="number"
                    value={formData.storageIncludedGB}
                    onChange={(e) =>
                      setFormData({ ...formData, storageIncludedGB: parseInt(e.target.value) || 0 })
                    }
                    required
                  />
                </div>
                <div>
                  <label htmlFor="plan-max-agents" className="block text-sm font-medium mb-1">
                    {t('planManagement.fields.maxAgents')}
                  </label>
                  <Input
                    id="plan-max-agents"
                    type="number"
                    value={formData.maxAgents || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        maxAgents: e.target.value ? parseInt(e.target.value) : undefined,
                      })
                    }
                    placeholder="∞"
                  />
                </div>
                <div>
                  <label
                    htmlFor="plan-extra-agent-price"
                    className="block text-sm font-medium mb-1"
                  >
                    {t('planManagement.fields.extraAgentPrice')}
                  </label>
                  <Input
                    id="plan-extra-agent-price"
                    type="number"
                    value={formData.extraAgentPrice || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        extraAgentPrice: e.target.value ? parseInt(e.target.value) : undefined,
                      })
                    }
                    placeholder={t('planManagement.placeholders.optional')}
                  />
                </div>
                <div>
                  <label
                    htmlFor="plan-extra-agent-storage"
                    className="block text-sm font-medium mb-1"
                  >
                    {t('planManagement.fields.extraAgentStorage')}
                  </label>
                  <Input
                    id="plan-extra-agent-storage"
                    type="number"
                    value={formData.extraAgentStorageGB || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        extraAgentStorageGB: e.target.value ? parseInt(e.target.value) : undefined,
                      })
                    }
                    placeholder="0"
                  />
                </div>
                <div>
                  <label
                    htmlFor="plan-extra-storage-price"
                    className="block text-sm font-medium mb-1"
                  >
                    {t('planManagement.fields.extraStoragePrice')}
                  </label>
                  <Input
                    id="plan-extra-storage-price"
                    type="number"
                    value={formData.extraStoragePrice}
                    onChange={(e) =>
                      setFormData({ ...formData, extraStoragePrice: parseInt(e.target.value) || 0 })
                    }
                  />
                </div>
                <div>
                  <label
                    htmlFor="plan-extra-storage-amount"
                    className="block text-sm font-medium mb-1"
                  >
                    {t('planManagement.fields.extraStorageAmount')}
                  </label>
                  <Input
                    id="plan-extra-storage-amount"
                    type="number"
                    value={formData.extraStorageAmountGB}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        extraStorageAmountGB: parseInt(e.target.value) || 10,
                      })
                    }
                  />
                </div>
                <div>
                  <label htmlFor="plan-base-margin" className="block text-sm font-medium mb-1">
                    {t('planManagement.fields.baseMargin')}
                  </label>
                  <Input
                    id="plan-base-margin"
                    type="number"
                    step="0.01"
                    value={formData.baseMargin || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        baseMargin: e.target.value ? parseFloat(e.target.value) : undefined,
                      })
                    }
                    placeholder={t('planManagement.placeholders.margin')}
                  />
                </div>

                {/* Features */}
                <div className="md:col-span-3 border-t pt-4 mt-2">
                  <h3 className="font-bold text-sm mb-3">{t('planManagement.features')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label
                        htmlFor="plan-retention-days"
                        className="block text-sm font-medium mb-1"
                      >
                        {t('planManagement.fields.retentionDays')}
                      </label>
                      <Input
                        id="plan-retention-days"
                        type="number"
                        value={formData.features.retentionDays}
                        onChange={(e) =>
                          updateFeature('retentionDays', parseInt(e.target.value) || 7)
                        }
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="plan-support-level"
                        className="block text-sm font-medium mb-1"
                      >
                        {t('planManagement.fields.supportLevel')}
                      </label>
                      <select
                        id="plan-support-level"
                        className="w-full min-h-11 px-3 py-2 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-lg"
                        value={formData.features.support}
                        onChange={(e) => updateFeature('support', e.target.value)}
                      >
                        {SUPPORT_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {t(`planManagement.support.${opt.value}`)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-center space-x-4 pt-6">
                      <label className="flex min-h-11 items-center gap-2">
                        <input
                          type="checkbox"
                          className="h-5 w-5 shrink-0"
                          checked={formData.features.apiAccess}
                          onChange={(e) => updateFeature('apiAccess', e.target.checked)}
                        />
                        <span className="text-sm">{t('planManagement.fields.apiAccess')}</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Stripe Integration */}
                <div className="md:col-span-3 border-t pt-4 mt-2">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-sm">{t('planManagement.stripeTitle')}</h3>
                    {stripeEnabled ? (
                      <Badge variant="success" className="text-xs">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {t('planManagement.stripeEnabled')}
                      </Badge>
                    ) : (
                      <Badge variant="neutral" className="text-xs">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        {t('planManagement.stripeNotConfigured')}
                      </Badge>
                    )}
                  </div>

                  {stripeEnabled && (
                    <div className="mb-4 p-3 bg-state-info-bg-light dark:bg-state-info-bg-dark rounded-lg">
                      <label className="flex min-h-11 items-center gap-2">
                        <input
                          type="checkbox"
                          className="h-5 w-5 shrink-0"
                          checked={formData.syncWithStripe}
                          onChange={(e) =>
                            setFormData({ ...formData, syncWithStripe: e.target.checked })
                          }
                        />
                        <span className="text-sm font-medium text-state-info-light dark:text-state-info-dark ">
                          {t('planManagement.stripeAutoSync')}
                        </span>
                      </label>
                      <p className="text-xs text-state-info-light dark:text-state-info-dark mt-1 ml-5">
                        {t('planManagement.stripeAutoSyncDescription')}
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="plan-stripe-price" className="block text-sm font-medium mb-1">
                        {t('planManagement.fields.stripePriceId')}
                      </label>
                      <Input
                        id="plan-stripe-price"
                        value={formData.stripePriceId || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, stripePriceId: e.target.value })
                        }
                        placeholder="price_..."
                        disabled={formData.syncWithStripe}
                      />
                      {formData.syncWithStripe && (
                        <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-1">
                          {t('planManagement.stripeAutoGenerated')}
                        </p>
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="plan-stripe-product"
                        className="block text-sm font-medium mb-1"
                      >
                        {t('planManagement.fields.stripeProductId')}
                      </label>
                      <Input
                        id="plan-stripe-product"
                        value={formData.stripeProductId || ''}
                        onChange={(e) =>
                          setFormData({ ...formData, stripeProductId: e.target.value })
                        }
                        placeholder="prod_..."
                        disabled={formData.syncWithStripe}
                      />
                      {formData.syncWithStripe && (
                        <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-1">
                          {t('planManagement.stripeAutoGenerated')}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Add-on Stripe IDs (read-only) */}
                  {editingPlan?.addonStripeIds?.agentAddonPriceId && (
                    <div className="mt-4 p-3 bg-surface-2-light dark:bg-surface-2-dark rounded-lg">
                      <h4 className="text-xs font-bold uppercase text-text-muted-light dark:text-text-muted-dark mb-2">
                        {t('planManagement.agentStripeIds')}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-text-subtle-light dark:text-text-subtle-dark">
                            {t('planManagement.productId')}:
                          </span>
                          <code className="ml-2 text-xs bg-surface-border-light dark:bg-surface-border-dark px-2 py-1 rounded">
                            {editingPlan.addonStripeIds.agentAddonProductId?.substring(0, 20)}...
                          </code>
                        </div>
                        <div>
                          <span className="text-text-subtle-light dark:text-text-subtle-dark">
                            {t('planManagement.priceId')}:
                          </span>
                          <code className="ml-2 text-xs bg-surface-border-light dark:bg-surface-border-dark px-2 py-1 rounded">
                            {editingPlan.addonStripeIds.agentAddonPriceId?.substring(0, 20)}...
                          </code>
                        </div>
                      </div>
                    </div>
                  )}

                  {editingPlan?.addonStripeIds?.storageAddonPriceId && (
                    <div className="mt-3 p-3 bg-surface-2-light dark:bg-surface-2-dark rounded-lg">
                      <h4 className="text-xs font-bold uppercase text-text-muted-light dark:text-text-muted-dark mb-2">
                        {t('planManagement.storageStripeIds')}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-text-subtle-light dark:text-text-subtle-dark">
                            {t('planManagement.productId')}:
                          </span>
                          <code className="ml-2 text-xs bg-surface-border-light dark:bg-surface-border-dark px-2 py-1 rounded">
                            {editingPlan.addonStripeIds.storageAddonProductId?.substring(0, 20)}...
                          </code>
                        </div>
                        <div>
                          <span className="text-text-subtle-light dark:text-text-subtle-dark">
                            {t('planManagement.priceId')}:
                          </span>
                          <code className="ml-2 text-xs bg-surface-border-light dark:bg-surface-border-dark px-2 py-1 rounded">
                            {editingPlan.addonStripeIds.storageAddonPriceId?.substring(0, 20)}...
                          </code>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Status */}
                <div className="md:col-span-3 flex items-center space-x-6 pt-4">
                  <label className="flex min-h-11 items-center space-x-2">
                    <input
                      type="checkbox"
                      className="h-5 w-5"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    <span>{t('planManagement.active')}</span>
                  </label>
                  <label className="flex min-h-11 items-center space-x-2">
                    <input
                      type="checkbox"
                      className="h-5 w-5"
                      checked={formData.isPublic}
                      onChange={(e) => setFormData({ ...formData, isPublic: e.target.checked })}
                    />
                    <span>{t('planManagement.publicOnPricing')}</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setShowForm(false);
                    setEditingPlan(null);
                  }}
                  disabled={saving}
                >
                  {t('planManagement.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={saving || !formData.name || !formData.displayName}
                  isLoading={saving}
                >
                  {editingPlan ? t('planManagement.updatePlan') : t('planManagement.createPlan')}
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Plans Table */}
        {plans.length > 0 && (
          <Card className="hidden xl:block overflow-x-auto">
            <table className="w-full min-w-[1100px]">
              <thead className="bg-surface-2-light dark:bg-surface-2-dark">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    {t('planManagement.table.plan')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    {t('planManagement.table.price')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    {t('planManagement.table.agents')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    {t('planManagement.table.storage')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    {t('planManagement.table.addons')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    {t('planManagement.table.status')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">
                    {t('planManagement.table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-border-light dark:divide-surface-border-dark">
                {plans.map((plan) => (
                  <tr
                    key={plan.id}
                    className="hover:bg-surface-2-light dark:hover:bg-surface-2-dark"
                  >
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-bold text-slate-900 dark:text-white">
                          {plan.displayName}
                        </p>
                        <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                          {plan.name}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="whitespace-nowrap text-sm font-bold">
                        {formatPlanPrice(plan.monthlyPrice, plan.currency)}
                      </span>
                      <span className="ml-1 whitespace-nowrap text-xs text-text-muted-light dark:text-text-muted-dark">
                        {t('planManagement.perMonth')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1">
                        <Users className="w-4 h-4 text-text-subtle-light dark:text-text-subtle-dark" />
                        <span>{plan.agentsIncluded}</span>
                        {plan.maxAgents && (
                          <span className="text-xs text-text-muted-light dark:text-text-muted-dark">
                            {t('planManagement.maxAgents', { count: plan.maxAgents })}
                          </span>
                        )}
                        {plan.hasUnlimitedAgents && (
                          <span className="text-xs text-state-ok-light dark:text-state-ok-dark">
                            ({t('planManagement.unlimited')})
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1">
                        <HardDrive className="w-4 h-4 text-text-subtle-light dark:text-text-subtle-dark" />
                        <span>{plan.storageIncludedGB} GB</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        {plan.hasExtraAgents && (
                          <p className="text-xs">
                            <Package className="w-3 h-3 inline mr-1" />+
                            {t('planManagement.agentAddon')}:{' '}
                            {formatPlanPrice(plan.extraAgentPrice!, plan.currency)}
                            {plan.extraAgentStorageGB && ` (+${plan.extraAgentStorageGB} GB)`}
                          </p>
                        )}
                        {plan.hasExtraStorage && (
                          <p className="text-xs">
                            <HardDrive className="w-3 h-3 inline mr-1" />+
                            {t('planManagement.storageAddon')}:{' '}
                            {formatPlanPrice(plan.extraStoragePrice, plan.currency)} /{' '}
                            {plan.extraStorageAmountGB} GB
                          </p>
                        )}
                        {!plan.hasExtraAgents && !plan.hasExtraStorage && (
                          <span className="text-xs text-text-subtle-light dark:text-text-subtle-dark">
                            —
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col space-y-1">
                        <div className="flex space-x-1">
                          {plan.isActive ? (
                            <Badge variant="success">
                              <Check className="w-3 h-3 mr-1" />
                              {t('planManagement.active')}
                            </Badge>
                          ) : (
                            <Badge variant="neutral">
                              <X className="w-3 h-3 mr-1" />
                              {t('planManagement.inactive')}
                            </Badge>
                          )}
                          {plan.isPublic && (
                            <Badge variant="info">{t('planManagement.public')}</Badge>
                          )}
                        </div>
                        {stripeEnabled && (
                          <div className="flex items-center space-x-1">
                            {plan.stripeProductId ? (
                              <Badge variant="success" className="text-[10px]">
                                <CreditCard className="w-3 h-3 mr-1" />
                                Stripe
                              </Badge>
                            ) : (
                              <Badge variant="neutral" className="text-[10px]">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                {t('planManagement.stripeNotConfigured')}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button
                          size="md"
                          variant="ghost"
                          onClick={() => startEdit(plan)}
                          title={t('planManagement.edit')}
                          aria-label={t('planManagement.editNamed', { name: plan.displayName })}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        {stripeEnabled && (
                          <Button
                            size="md"
                            variant="ghost"
                            onClick={() => handleSyncWithStripe(plan)}
                            disabled={syncingPlanId === plan.id}
                            title={t('planManagement.syncStripe')}
                            aria-label={t('planManagement.syncNamed', { name: plan.displayName })}
                          >
                            {syncingPlanId === plan.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4 text-state-info-light dark:text-state-info-dark" />
                            )}
                          </Button>
                        )}
                        <Button
                          size="md"
                          variant="ghost"
                          onClick={() => handleDelete(plan)}
                          title={t('planManagement.delete')}
                          aria-label={t('planManagement.deleteNamed', { name: plan.displayName })}
                        >
                          <Trash2 className="w-4 h-4 text-state-danger-light dark:text-state-danger-dark" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {plans.length > 0 && (
          <div className="xl:hidden space-y-4">
            {plans.map((plan) => (
              <Card key={plan.id} className="p-4 space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="font-bold text-slate-900 dark:text-white">{plan.displayName}</h2>
                    <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                      {plan.name}
                    </p>
                  </div>
                  <div className="flex flex-wrap justify-end gap-1">
                    <Badge variant={plan.isActive ? 'success' : 'neutral'}>
                      {plan.isActive ? t('planManagement.active') : t('planManagement.inactive')}
                    </Badge>
                    {plan.isPublic && <Badge variant="info">{t('planManagement.public')}</Badge>}
                  </div>
                </div>

                <p className="text-lg font-bold text-slate-900 dark:text-white">
                  {formatPlanPrice(plan.monthlyPrice, plan.currency)}
                  <span className="ml-1 text-sm font-medium text-text-muted-light dark:text-text-muted-dark">
                    {t('planManagement.perMonth')}
                  </span>
                </p>

                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-xs text-text-muted-light dark:text-text-muted-dark">
                      {t('planManagement.table.agents')}
                    </dt>
                    <dd className="font-semibold">
                      {plan.agentsIncluded}
                      {plan.maxAgents
                        ? ` ${t('planManagement.maxAgents', { count: plan.maxAgents })}`
                        : plan.hasUnlimitedAgents
                          ? ` (${t('planManagement.unlimited')})`
                          : ''}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-text-muted-light dark:text-text-muted-dark">
                      {t('planManagement.table.storage')}
                    </dt>
                    <dd className="font-semibold">{plan.storageIncludedGB} GB</dd>
                  </div>
                </dl>

                {(plan.hasExtraAgents || plan.hasExtraStorage) && (
                  <div className="space-y-1 border-t border-surface-border-light dark:border-surface-border-dark pt-3 text-sm">
                    {plan.hasExtraAgents && (
                      <p>
                        +{t('planManagement.agentAddon')}:{' '}
                        {formatPlanPrice(plan.extraAgentPrice!, plan.currency)}
                        {plan.extraAgentStorageGB ? ` (+${plan.extraAgentStorageGB} GB)` : ''}
                      </p>
                    )}
                    {plan.hasExtraStorage && (
                      <p>
                        +{t('planManagement.storageAddon')}:{' '}
                        {formatPlanPrice(plan.extraStoragePrice, plan.currency)} /{' '}
                        {plan.extraStorageAmountGB} GB
                      </p>
                    )}
                  </div>
                )}

                {stripeEnabled && (
                  <div className="flex gap-2">
                    <Badge variant={plan.stripeProductId ? 'success' : 'neutral'}>
                      {plan.stripeProductId ? 'Stripe' : t('planManagement.stripeNotConfigured')}
                    </Badge>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 border-t border-surface-border-light dark:border-surface-border-dark pt-3">
                  <Button
                    size="md"
                    variant="secondary"
                    onClick={() => startEdit(plan)}
                    aria-label={t('planManagement.editNamed', { name: plan.displayName })}
                  >
                    <Edit2 className="mr-2 h-4 w-4" />
                    {t('planManagement.edit')}
                  </Button>
                  {stripeEnabled && (
                    <Button
                      size="md"
                      variant="secondary"
                      onClick={() => handleSyncWithStripe(plan)}
                      disabled={syncingPlanId === plan.id}
                      aria-label={t('planManagement.syncNamed', { name: plan.displayName })}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      {t('planManagement.syncStripe')}
                    </Button>
                  )}
                  <Button
                    size="md"
                    variant="ghost"
                    onClick={() => handleDelete(plan)}
                    aria-label={t('planManagement.deleteNamed', { name: plan.displayName })}
                  >
                    <Trash2 className="mr-2 h-4 w-4 text-state-danger-light dark:text-state-danger-dark" />
                    {t('planManagement.delete')}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
